import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Clock,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
  Award,
  BookOpen,
  Flag
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TestTaking = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  
  const [test, setTest] = useState(null);
  const [attemptId, setAttemptId] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [markedForReview, setMarkedForReview] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    startTest();
  }, [testId]);

  useEffect(() => {
    if (timeRemaining > 0) {
      const timer = setTimeout(() => {
        setTimeRemaining(timeRemaining - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeRemaining === 0 && test && !submitting && !showResults) {
      // Auto-submit when time runs out
      handleSubmit();
    }
  }, [timeRemaining]);

  const startTest = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Start test attempt
      const response = await axios.post(
        `${API}/test-prep/tests/${testId}/start`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setTest(response.data.test);
        setAttemptId(response.data.attempt_id);
        setTimeRemaining(response.data.test.duration_minutes * 60);
        toast.success('Test started! Good luck!');
      }
    } catch (error) {
      console.error('Error starting test:', error);
      toast.error('Failed to start test');
      navigate('/test-prep');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = async (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
    
    // Save answer to backend
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/test-prep/attempts/${attemptId}/answer`,
        { question_id: questionId, answer },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error saving answer:', error);
    }
  };

  const handleSubmit = async () => {
    if (!window.confirm('Are you sure you want to submit? You cannot change your answers after submission.')) {
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API}/test-prep/attempts/${attemptId}/submit`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setResults(response.data);
        setShowResults(true);
        toast.success('Test submitted successfully!');
      }
    } catch (error) {
      console.error('Error submitting test:', error);
      toast.error('Failed to submit test');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading test...</p>
        </div>
      </div>
    );
  }

  if (showResults && results) {
    return <TestResults results={results} test={test} onBackToTests={() => navigate('/test-prep')} />;
  }

  if (!test || !test.questions || test.questions.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Test Not Found</h3>
            <p className="text-gray-600 mb-6">Unable to load test questions</p>
            <Button onClick={() => navigate('/test-prep')}>Back to Tests</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentQuestion = test.questions[currentQuestionIndex];
  const answeredCount = Object.keys(answers).length;
  const progress = (answeredCount / test.questions.length) * 100;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h1 className="text-xl font-bold text-gray-900">{test.name}</h1>
              <Badge variant="outline">{test.exam_type}</Badge>
            </div>
            
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <span className="text-sm text-gray-600">
                  {answeredCount}/{test.questions.length} Answered
                </span>
              </div>
              
              <div className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
                timeRemaining < 300 ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
              }`}>
                <Clock className="h-5 w-5" />
                <span className="font-mono font-semibold">{formatTime(timeRemaining)}</span>
              </div>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-orange-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Question Panel */}
          <div className="lg:col-span-3">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">
                    Question {currentQuestionIndex + 1} of {test.questions.length}
                  </CardTitle>
                  <div className="flex items-center space-x-2">
                    <Badge className={
                      currentQuestion.difficulty === 'Easy' ? 'bg-green-100 text-green-800' :
                      currentQuestion.difficulty === 'Hard' ? 'bg-red-100 text-red-800' :
                      'bg-blue-100 text-blue-800'
                    }>
                      {currentQuestion.difficulty}
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setMarkedForReview(prev => ({
                        ...prev,
                        [currentQuestion.id]: !prev[currentQuestion.id]
                      }))}
                    >
                      <Flag className={`h-4 w-4 ${markedForReview[currentQuestion.id] ? 'text-orange-600' : ''}`} />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-gray-900 text-lg font-medium leading-relaxed">
                  {currentQuestion.question_text}
                </p>

                {/* Answer Options */}
                <div className="space-y-3">
                  {currentQuestion.options && currentQuestion.options.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => handleAnswerSelect(currentQuestion.id, option.id)}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                        answers[currentQuestion.id] === option.id
                          ? 'border-orange-600 bg-orange-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          answers[currentQuestion.id] === option.id
                            ? 'border-orange-600 bg-orange-600'
                            : 'border-gray-300'
                        }`}>
                          {answers[currentQuestion.id] === option.id && (
                            <CheckCircle2 className="h-4 w-4 text-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <span className="font-semibold text-gray-700 mr-2">{option.id}.</span>
                          <span className="text-gray-900">{option.text}</span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Navigation Buttons */}
                <div className="flex items-center justify-between pt-6 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))}
                    disabled={currentQuestionIndex === 0}
                  >
                    <ChevronLeft className="h-4 w-4 mr-2" />
                    Previous
                  </Button>

                  {currentQuestionIndex === test.questions.length - 1 ? (
                    <Button
                      onClick={handleSubmit}
                      disabled={submitting}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {submitting ? 'Submitting...' : 'Submit Test'}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setCurrentQuestionIndex(Math.min(test.questions.length - 1, currentQuestionIndex + 1))}
                      className="bg-orange-600 hover:bg-orange-700"
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Question Navigator */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle className="text-sm">Question Navigator</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-5 gap-2">
                  {test.questions.map((q, idx) => (
                    <button
                      key={q.id}
                      onClick={() => setCurrentQuestionIndex(idx)}
                      className={`aspect-square rounded-lg text-sm font-medium transition-all ${
                        idx === currentQuestionIndex
                          ? 'bg-orange-600 text-white'
                          : answers[q.id]
                          ? 'bg-green-100 text-green-800 border-2 border-green-300'
                          : markedForReview[q.id]
                          ? 'bg-orange-100 text-orange-800 border-2 border-orange-300'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {idx + 1}
                    </button>
                  ))}
                </div>

                <div className="mt-4 space-y-2 text-xs">
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-green-100 border-2 border-green-300 rounded"></div>
                    <span className="text-gray-600">Answered</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-orange-100 border-2 border-orange-300 rounded"></div>
                    <span className="text-gray-600">Marked</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-gray-100 rounded"></div>
                    <span className="text-gray-600">Not Answered</span>
                  </div>
                </div>

                <Button 
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full mt-6 bg-green-600 hover:bg-green-700"
                >
                  Submit Test
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

// Test Results Component
const TestResults = ({ results, test, onBackToTests }) => {
  const passedClass = results.passed ? 'text-green-600' : 'text-red-600';
  const passedBg = results.passed ? 'bg-green-50' : 'bg-red-50';
  const passedBorder = results.passed ? 'border-green-200' : 'border-red-200';

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <Card className={`border-2 ${passedBorder} ${passedBg} mb-6`}>
          <CardContent className="pt-8 text-center">
            <div className={`w-20 h-20 rounded-full ${passedBg} flex items-center justify-center mx-auto mb-4`}>
              {results.passed ? (
                <Award className={`h-10 w-10 ${passedClass}`} />
              ) : (
                <AlertCircle className={`h-10 w-10 ${passedClass}`} />
              )}
            </div>
            <h1 className={`text-3xl font-bold ${passedClass} mb-2`}>
              {results.passed ? 'Congratulations! You Passed!' : 'Keep Practicing!'}
            </h1>
            <p className="text-gray-600 mb-6">
              {test.name}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
              <div className="bg-white rounded-lg p-4 border-2">
                <p className="text-sm text-gray-600">Score</p>
                <p className="text-2xl font-bold text-gray-900">
                  {results.score}/{results.total_questions}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 border-2">
                <p className="text-sm text-gray-600">Percentage</p>
                <p className="text-2xl font-bold text-gray-900">{results.percentage}%</p>
              </div>
              <div className="bg-white rounded-lg p-4 border-2">
                <p className="text-sm text-gray-600">Time Taken</p>
                <p className="text-2xl font-bold text-gray-900">{results.time_taken_minutes} min</p>
              </div>
              <div className="bg-white rounded-lg p-4 border-2">
                <p className="text-sm text-gray-600">Pass Score</p>
                <p className="text-2xl font-bold text-gray-900">{test.passing_score}%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Detailed Results */}
        <Card>
          <CardHeader>
            <CardTitle>Question-by-Question Review</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {results.results && results.results.map((result, idx) => (
              <Card key={idx} className={`border-2 ${result.is_correct ? 'border-green-200' : 'border-red-200'}`}>
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-3 mb-4">
                    {result.is_correct ? (
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-1" />
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 mb-2">
                        Question {idx + 1}: {result.question_text}
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                        <div>
                          <p className="text-sm text-gray-600">Your Answer:</p>
                          <p className={`font-semibold ${result.is_correct ? 'text-green-700' : 'text-red-700'}`}>
                            {result.user_answer || 'Not Answered'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Correct Answer:</p>
                          <p className="font-semibold text-green-700">{result.correct_answer}</p>
                        </div>
                      </div>
                      {result.explanation && (
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-sm text-gray-600 font-semibold mb-1">Explanation:</p>
                          <p className="text-sm text-gray-700">{result.explanation}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>

        <div className="mt-6 flex justify-center space-x-4">
          <Button onClick={onBackToTests} variant="outline">
            <BookOpen className="h-4 w-4 mr-2" />
            Back to Tests
          </Button>
          <Button onClick={() => window.location.reload()} className="bg-orange-600 hover:bg-orange-700">
            <ChevronRight className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TestTaking;
